import { DATA_SET_PAGINATION_COUNT } from 'constants/dataSets';
import { API_METHODS, TASK_SORT_BY } from 'constants/general';
import {
  MATERIALITY_IRO_TEMPLATES, STANDARDS_IRO_TEMPLATE_REQUESTS,
  STANDARDS_IRO_TEMPLATE_REQUESTS_APPROVE, STANDARDS_IRO_TEMPLATE_REQUESTS_DETAILS,
  STANDARDS_IRO_TEMPLATES_DETAILS,
} from 'constants/requestUrls';
import mainApi from 'store/apis/mainApi';
import TEMPLATES_LIST_TAG, { TEMPLATES_REQUEST_TAG } from 'store/tags/iroStandardsTags';

import type IIROImpactItem from 'store/slices/materialAssessment/interfaces/IIROImpactItem';
import type IIROTemplatesRequest from 'store/slices/materialAssessment/interfaces/IIROTemplatesRequest';
import type ITemplateItem from 'store/slices/materialAssessment/interfaces/ITemplateItem';
import type { IBaseQueryPaginationResponse } from 'store/types/IBaseQueryPaginationResponse';
import type IBaseQueryResponse from 'store/types/IBaseQueryResponse';
import type IIRORequestRequest from '../interfaces/IIRORequestRequest';
import type IRejectRequestRequest from '../interfaces/IRejectRequestRequest';
import type IRequestItem from '../interfaces/IRequestItem';
import type IUpdateTemplateRequest from '../interfaces/IUpdateTemplateRequest';

export const iroStandardsAdminApi = mainApi.injectEndpoints({
  endpoints: (build) => ({
    getDefaultTemplatesList: build.query<
    IBaseQueryPaginationResponse<ITemplateItem[]>, IIROTemplatesRequest>({
      query: ({
        offset, standardId, naceSections, categories, description,
      }) => ({
        url: MATERIALITY_IRO_TEMPLATES,
        params: {
          ...(offset ? { offset } : { offset: 0 }),
          ...(naceSections && naceSections.length > 0 ? { naceSections } : {}),
          ...(description ? { description } : {}),
          limit: DATA_SET_PAGINATION_COUNT,
          orderBy: TASK_SORT_BY.CREATED_AT,
          standardId,
          ...(categories && categories.length > 0 ? { categories } : {}),
        },
      }),
      providesTags: (result) => (
        result?.data?.items?.length && result?.data?.items?.length > 0
          ? [...result.data.items.map(({ id }) => (
            { type: TEMPLATES_LIST_TAG, id } as const)), TEMPLATES_LIST_TAG]
          : [TEMPLATES_LIST_TAG]
      ),
    }),
    getIRORequestList: build.query<
    IBaseQueryPaginationResponse<IRequestItem[]>, IIRORequestRequest>({
      query: ({ offset, status }) => ({
        url: STANDARDS_IRO_TEMPLATE_REQUESTS,
        params: {
          ...(offset ? { offset } : { offset: 0 }),
          limit: DATA_SET_PAGINATION_COUNT,
          status,
        },
      }),
      providesTags: (result) => (
        result?.data?.items?.length && result?.data?.items?.length > 0
          ? [...result.data.items.map(({ id }) => (
            { type: TEMPLATES_REQUEST_TAG, id } as const)), TEMPLATES_REQUEST_TAG]
          : [TEMPLATES_REQUEST_TAG]
      ),
    }),
    rejectRequestTemplates: build.mutation <null, IRejectRequestRequest>({
      query: ({ id }) => ({
        url: STANDARDS_IRO_TEMPLATE_REQUESTS_DETAILS.replace('{id}', `${id}`),
        method: API_METHODS.delete,
      }),
      invalidatesTags: [TEMPLATES_REQUEST_TAG, TEMPLATES_LIST_TAG],
    }),
    approveRequestTemplates: build.mutation <IBaseQueryResponse<ITemplateItem>, IRejectRequestRequest>({
      query: ({ id }) => ({
        url: STANDARDS_IRO_TEMPLATE_REQUESTS_APPROVE.replace('{id}', `${id}`),
        method: API_METHODS.post,
      }),
      invalidatesTags: [TEMPLATES_REQUEST_TAG, TEMPLATES_LIST_TAG],
    }),
    deleteTemplate: build.mutation <null, IRejectRequestRequest>({
      query: ({ id }) => ({
        url: STANDARDS_IRO_TEMPLATES_DETAILS.replace('{id}', `${id}`),
        method: API_METHODS.delete,
      }),
      invalidatesTags: [],
    }),
    updateTemplate: build.mutation <IBaseQueryResponse<IIROImpactItem>, IUpdateTemplateRequest>({
      query: ({ data, id }) => ({
        url: STANDARDS_IRO_TEMPLATES_DETAILS.replace('{id}', `${id}`),
        method: API_METHODS.patch,
        body: data,
      }),
      invalidatesTags: [TEMPLATES_LIST_TAG],
    }),
  }),
});

export const {
  useGetDefaultTemplatesListQuery,
  useGetIRORequestListQuery,
  useRejectRequestTemplatesMutation,
  useApproveRequestTemplatesMutation,
  useDeleteTemplateMutation,
  useUpdateTemplateMutation,
} = iroStandardsAdminApi;
